/**
 * Weather Legend Context
 * Manages which DTN weather layer's legend is currently displayed
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useDTNLayerLegend } from '@ais-ui/dtn-weather-api';
import type { DTNLegend } from '@ais-ui/dtn-weather-api';

interface WeatherLegendState {
  activeLayerId: string | null;
  activeLayerLabel: string | null;
  legend: DTNLegend[] | null;
  isLoading: boolean;
  showLegend: boolean;
}

interface WeatherLegendContextValue extends WeatherLegendState {
  setActiveLegend: (layerId: string, layerLabel: string) => void;
  clearLegend: () => void;
  toggleLegend: (layerId: string, layerLabel: string) => void;
  isLegendActiveForLayer: (layerId: string) => boolean;
}

const WeatherLegendContext = createContext<WeatherLegendContextValue | null>(null);

interface WeatherLegendProviderProps {
  children: React.ReactNode;
}

export const WeatherLegendProvider: React.FC<WeatherLegendProviderProps> = ({ children }) => {
  const [activeLayerId, setActiveLayerId] = useState<string | null>(null);
  const [activeLayerLabel, setActiveLayerLabel] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(false);

  // Fetch legend for the active layer using proxy (no token needed)
  const { data: legendData, isLoading } = useDTNLayerLegend(
    undefined, // token - ignored, handled by proxy
    activeLayerId || undefined,
    undefined, // unit
    'en-US'   // locale
  );

  const setActiveLegend = useCallback((layerId: string, layerLabel: string) => {
    setActiveLayerId(layerId);
    setActiveLayerLabel(layerLabel);
    setShowLegend(true);
  }, []);

  const clearLegend = useCallback(() => {
    setActiveLayerId(null);
    setActiveLayerLabel(null);
    setShowLegend(false);
  }, []);

  const toggleLegend = useCallback((layerId: string, layerLabel: string) => {
    if (activeLayerId === layerId && showLegend) {
      // Same layer, toggle off
      setShowLegend(false);
    } else {
      // Different layer or was off, set and show
      setActiveLayerId(layerId);
      setActiveLayerLabel(layerLabel);
      setShowLegend(true);
    }
  }, [activeLayerId, showLegend]);

  const isLegendActiveForLayer = useCallback((layerId: string) => {
    return activeLayerId === layerId && showLegend;
  }, [activeLayerId, showLegend]);

  const value: WeatherLegendContextValue = {
    activeLayerId,
    activeLayerLabel,
    legend: legendData || null,
    isLoading,
    showLegend: showLegend && !!legendData && legendData.length > 0,
    setActiveLegend,
    clearLegend,
    toggleLegend,
    isLegendActiveForLayer,
  };

  return (
    <WeatherLegendContext.Provider value={value}>
      {children}
    </WeatherLegendContext.Provider>
  );
};

export const useWeatherLegend = (): WeatherLegendContextValue => {
  const context = useContext(WeatherLegendContext);
  if (!context) {
    throw new Error('useWeatherLegend must be used within a WeatherLegendProvider');
  }
  return context;
};

export default WeatherLegendContext;
